import { Link } from 'react-router-dom';
import { Header } from './components/Header';

const MCP_CONFIG = `{
  "mcpServers": {
    "mailcrunch": {
      "type": "stdio",
      "command": "npx",
      "args": ["@mailcrunch/mcp-server"]
    }
  }
}`;

type Tool = {
  name: string;
  description: string;
  params?: string;
};

const readTools: Tool[] = [
  { name: 'list_connected_projects', description: 'Returns all currently connected Mailcrunch editor tabs.' },
  { name: 'read_source', description: 'Returns the MJML+EJS template source from the editor.', params: 'startLine?, endLine?' },
  { name: 'search_source', description: 'Regex search across template source with context lines.', params: 'pattern, contextLines?' },
  { name: 'read_output_html', description: 'Returns the compiled HTML output of the current template.', params: 'startLine?, endLine?' },
  { name: 'get_compilation_errors', description: 'Returns current MJML/EJS compilation errors and warnings.' },
  { name: 'get_template_helpers', description: 'Returns available EJS template helpers with signatures and descriptions.' },
  { name: 'list_assets', description: 'Returns available image assets with their URLs.' },
  { name: 'get_revision_history', description: 'Returns previous saved revisions of the template.', params: 'limit?' },
];

const writeTools: Tool[] = [
  { name: 'edit_source', description: 'Replaces lines in the Monaco editor. Triggers live preview update. Supports optimistic concurrency via contentHash.', params: 'newContent, startLine?, endLine?, contentHash?' },
  { name: 'trigger_save', description: 'Saves the current editor state as a new revision.' },
  { name: 'trigger_export', description: 'Exports the compiled HTML with current settings.' },
];

const ToolRow = ({ tool, badge }: { tool: Tool; badge: 'read' | 'write' }) => (
  <tr className="border-t border-gray-100">
    <td className="py-2.5 pr-4 align-top w-56">
      <code className="text-xs font-mono font-semibold text-gray-800">{tool.name}</code>
    </td>
    <td className="py-2.5 align-top">
      <span className="text-sm text-gray-500">{tool.description}</span>
      {tool.params && (
        <span className="ml-2 text-xs font-mono text-gray-300">{tool.params}</span>
      )}
    </td>
  </tr>
);

const Connector = ({ label, bidirectional = false }: { label: string; bidirectional?: boolean }) => (
  <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
    <div className="w-full flex items-center">
      {bidirectional && (
        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" className="shrink-0 text-gray-300" style={{ transform: 'scaleX(-1)' }}>
          <path d="M1 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      <div className="flex-1 h-px bg-gray-200" />
      <svg width="6" height="10" viewBox="0 0 6 10" fill="none" className="shrink-0 text-gray-300">
        <path d="M1 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
    <span className="text-[11px] text-gray-400 font-mono whitespace-nowrap">{label}</span>
  </div>
);

const HowItWorks = () => (
  <section className="mb-16">
    <h2 className="text-xl font-bold text-gray-900 mb-6">How it works</h2>
    <div className="flex items-center gap-2">

      {/* Node: AI Agent */}
      <div className="shrink-0 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-center">
        <div className="text-sm font-semibold text-gray-800">AI agent</div>
        <div className="text-xs text-gray-400 mt-0.5">Claude Code, Cursor…</div>
      </div>

      <Connector label="stdio" />

      {/* Node: MCP Server */}
      <div className="shrink-0 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-center">
        <div className="text-sm font-semibold text-gray-800">MCP Server</div>
        <div className="text-xs text-gray-400 mt-0.5 font-mono">@mailcrunch/mcp-server</div>
      </div>

      <Connector label="WebSocket" bidirectional />

      {/* Node: Browser Tab */}
      <div className="shrink-0 rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="flex items-center gap-1 px-3 py-2 bg-gray-50 border-b border-gray-200">
          <span className="w-2 h-2 rounded-full bg-gray-200" />
          <span className="w-2 h-2 rounded-full bg-gray-200" />
          <span className="w-2 h-2 rounded-full bg-gray-200" />
          <div className="ml-1 flex-1 bg-white border border-gray-200 rounded text-[10px] text-gray-400 px-1.5 py-0.5 font-mono truncate">
            mailcrunch.io/…
          </div>
        </div>
        <div className="px-4 py-3 text-center">
          <div className="text-sm font-semibold text-gray-800">Mailcrunch</div>
          <div className="text-xs text-gray-400 mt-0.5">live editor tab</div>
        </div>
      </div>

    </div>
  </section>
);

const Step = ({ n, title, children }: { n: number; title: string; children: React.ReactNode }) => (
  <div className="pb-7">
    <p className="text-sm font-semibold text-gray-800 mb-3">
      <span className="text-gray-400 mr-2">{n}.</span>{title}
    </p>
    <div className="ml-5">
      {children}
    </div>
  </div>
);

export const McpPage = () => {
  return (
    <div className="min-h-screen w-full bg-white font-sans antialiased">
      <Header />

      <main className="pt-28 pb-24 px-6">
        <div className="max-w-3xl mx-auto">

          {/* Hero */}
          <div className="mb-16 text-center">
            <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-full bg-gray-100 text-gray-500 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
              @mailcrunch/mcp-server
            </div>
            <h1 className="text-4xl font-bold text-gray-900 tracking-tight mb-4">
              Mailcrunch MCP
            </h1>
            <p className="text-lg text-gray-500 leading-relaxed max-w-xl mx-auto">
              <span className="font-semibold text-gray-800">Let coding agents read and edit your email templates directly.</span>{' '}
              Open a project in Mailcrunch, enable the MCP bridge, and your agent can read source, search, make edits, and trigger saves — all through the live editor.
            </p>
          </div>

          <HowItWorks />

          {/* Setup */}
          <section className="mb-16">
            <h2 className="text-xl font-bold text-gray-900 mb-8">Setup</h2>

            <div>

              <Step n={1} title="Add the MCP server">
                <div className="rounded-xl overflow-hidden border border-gray-200 mb-3">
                  <div className="flex items-center justify-between px-4 py-2 bg-gray-950 border-b border-gray-800">
                    <span className="text-xs text-gray-500">Claude Code</span>
                  </div>
                  <pre className="bg-gray-950 text-gray-100 text-sm font-mono px-5 py-4 overflow-x-auto">
                    <span className="text-gray-500">$</span>{' '}
                    <span className="text-green-400">claude mcp add</span>
                    {' mailcrunch -- npx @mailcrunch/mcp-server'}
                  </pre>
                </div>
                <details className="group">
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 transition-colors list-none flex items-center gap-1">
                    <svg className="w-3 h-3 transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    Other clients (Cursor, Windsurf…)
                  </summary>
                  <div className="mt-2 rounded-xl overflow-hidden border border-gray-200">
                    <div className="flex items-center px-4 py-2 bg-gray-950 border-b border-gray-800">
                      <span className="text-xs text-gray-500 font-mono">.mcp.json</span>
                    </div>
                    <pre className="bg-gray-950 text-gray-100 text-sm font-mono p-5 overflow-x-auto leading-relaxed">
                      <span className="text-gray-500">{'{'}</span>{'\n'}
                      {'  '}<span className="text-blue-400">"mcpServers"</span><span className="text-gray-500">: {'{'}</span>{'\n'}
                      {'    '}<span className="text-blue-400">"mailcrunch"</span><span className="text-gray-500">: {'{'}</span>{'\n'}
                      {'      '}<span className="text-blue-400">"type"</span><span className="text-gray-500">:</span>{' '}<span className="text-green-400">"stdio"</span><span className="text-gray-500">,</span>{'\n'}
                      {'      '}<span className="text-blue-400">"command"</span><span className="text-gray-500">:</span>{' '}<span className="text-green-400">"npx"</span><span className="text-gray-500">,</span>{'\n'}
                      {'      '}<span className="text-blue-400">"args"</span><span className="text-gray-500">:</span>{' '}<span className="text-gray-500">[</span><span className="text-green-400">"@mailcrunch/mcp-server"</span><span className="text-gray-500">]</span>{'\n'}
                      {'    '}<span className="text-gray-500">{'}'}</span>{'\n'}
                      {'  '}<span className="text-gray-500">{'}'}</span>{'\n'}
                      <span className="text-gray-500">{'}'}</span>
                    </pre>
                  </div>
                </details>
              </Step>

              <Step n={2} title="Open a Mailcrunch project in your browser">
                <p className="text-sm text-gray-500 leading-relaxed">
                  Navigate to any project in the editor. The MCP server connects to your open browser tab via a local WebSocket — edits flow through the editor, not directly to the database.
                </p>
              </Step>

              <Step n={3} title="Enable the MCP Bridge in the toolbar">
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700">
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z" />
                  </svg>
                  MCP Bridge
                  <span className="w-2 h-2 rounded-full bg-gray-300" />
                </div>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                  Click the MCP Bridge toggle in the Mailcrunch toolbar. The dot turns green when your agent is connected. One browser tab per project — if you open the same project in multiple tabs, tool calls route to the most recently active one.
                </p>
              </Step>
            </div>
          </section>

          {/* Tools */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Tools</h2>
            <p className="text-sm text-gray-400 mb-6">
              All tools accept optional <code className="text-gray-500 bg-gray-50 px-1 py-0.5 rounded text-xs">orgSlug</code> and <code className="text-gray-500 bg-gray-50 px-1 py-0.5 rounded text-xs">projectSlug</code> — auto-resolved when only one project is connected.
            </p>

            <table className="w-full text-left border-collapse">
              <tbody>
                <tr>
                  <td colSpan={2} className="pb-1 pt-4">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Read</span>
                  </td>
                </tr>
                {readTools.map((tool) => (
                  <ToolRow key={tool.name} tool={tool} badge="read" />
                ))}
                <tr>
                  <td colSpan={2} className="pb-1 pt-6">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Write</span>
                  </td>
                </tr>
                {writeTools.map((tool) => (
                  <ToolRow key={tool.name} tool={tool} badge="write" />
                ))}
              </tbody>
            </table>
          </section>

          {/* Footer note */}
          <div className="mt-16 pt-8 border-t border-gray-100 flex items-center justify-between">
            <Link to="/" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">
              ← Back to Mailcrunch
            </Link>
            <a
              href="https://www.npmjs.com/package/@mailcrunch/mcp-server"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors font-mono"
            >
              npm: @mailcrunch/mcp-server
            </a>
          </div>

        </div>
      </main>
    </div>
  );
};
